import {
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  createContext,
} from "react";
import "./dialog-styles.css";
import { Box, Flex, Text } from "@radix-ui/themes";
import { AccountContext } from "../App.tsx";
import { ChatType, sendDirectMessage, sendSayMessage } from "../api.ts";
import { registerChatMessageHandler } from "../websocketService.ts";

// Chat channels the player can pick. DM needs a recipient; the rest are area
// chat with widening reach (local -> visible -> whole map).
type ChatMode = "say" | "shout" | "broadcast" | "dm";
const CHAT_MODES: { mode: ChatMode; label: string }[] = [
  { mode: "say", label: "Say" },
  { mode: "shout", label: "Shout" },
  { mode: "broadcast", label: "Broadcast" },
  { mode: "dm", label: "DM" },
];

export const ChatContext = createContext<{
  messages: string[];
  addMessage: (message: string) => void;
}>({
  messages: [],
  addMessage: () => {},
});

function ChatDialog({ websocket }: { websocket?: WebSocket }) {
  const [account] = useContext(AccountContext);
  const [messages, setMessages] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [chatMode, setChatMode] = useState<ChatMode>("say");
  const [dmRecipient, setDmRecipient] = useState("");
  const isDM = chatMode === "dm";
  const [isVisible, setIsVisible] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Function to add a message to the chat
  const addMessage = useCallback((message: string) => {
    setMessages((prevMessages) => [...prevMessages, message]);
  }, []);

  // Check viewport dimensions for visibility
  useEffect(() => {
    const checkViewportSize = () => {
      const viewportHeight = window.innerHeight;
      setIsVisible(viewportHeight >= 600);
    };

    checkViewportSize();
    window.addEventListener("resize", checkViewportSize);

    return () => {
      window.removeEventListener("resize", checkViewportSize);
    };
  }, []);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Register the addMessage function with the WebSocket service
  useEffect(() => {
    // Register the addMessage function to receive chat messages from the server
    registerChatMessageHandler(addMessage);

    // No need to unregister as the handler will be overwritten if the component remounts
  }, [addMessage]);

  // Handle sending a message
  const handleSendMessage = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && inputValue.trim() && account && websocket) {
      if (isDM) {
        if (!dmRecipient.trim()) return;
        // Local echo; the server does NOT echo DMs back to the sender.
        addMessage(`>> [dm to ${dmRecipient}] ${inputValue}`);
        sendDirectMessage(websocket, dmRecipient, inputValue);
      } else {
        // Area chat: the server echoes it back to us (labeled), so we do NOT
        // add a local copy here or the sender would see their line twice.
        const chatType =
          chatMode === "shout"
            ? ChatType.Shout
            : chatMode === "broadcast"
              ? ChatType.Broadcast
              : ChatType.Say;
        sendSayMessage(websocket, inputValue, chatType);
      }

      // Clear input after sending
      setInputValue("");
    }
  };

  // Toggle between minimized and maximized states
  const toggleMinimized = () => {
    setIsMinimized(!isMinimized);
  };

  // Calculate dimensions based on viewport
  const getWindowStyle = () => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Base styles
    const style: React.CSSProperties = {
      position: "fixed",
      right: "20px",
      bottom: "20px",
      backgroundColor: "rgba(0, 0, 0, 0.96)",
      color: "white",
      border: "1px solid var(--ro-border, #4a4a4a)",
      borderRadius: "8px",
      padding: "10px",
      display: "flex",
      flexDirection: "column",
      zIndex: 1000,
    };

    if (isMinimized) {
      // Minimized state - just enough to show the maximize icon
      style.width = "40px";
      style.height = "40px";
      style.padding = "5px";
    } else {
      // Maximized state - full size
      style.height = `${Math.floor(viewportHeight / 4)}px`; // Changed from 1/6 to 1/4

      // Adjust width based on viewport width
      if (viewportWidth < 1000) {
        style.width = `${Math.floor(viewportWidth / 2)}px`;
      } else {
        style.width = `${Math.floor(viewportWidth / 3)}px`;
      }
    }

    return style;
  };

  // Don't render if viewport height is less than 600px
  if (!isVisible || !account) {
    return null;
  }

  return (
    <ChatContext.Provider value={{ messages, addMessage }}>
      <Box style={getWindowStyle()}>
        {/* Toggle icon in the top right corner */}
        <Box
          style={{
            position: "absolute",
            top: "5px",
            right: "10px",
            cursor: "pointer",
            fontSize: "16px",
            fontWeight: "bold",
            zIndex: 1001,
          }}
          onClick={toggleMinimized}
        >
          {isMinimized ? "O" : "X"}
        </Box>

        {/* Only show content when not minimized */}
        {!isMinimized && (
          <>
            <Box
              style={{
                flex: 1,
                overflowY: "auto",
                marginBottom: "10px",
                padding: "5px",
                marginTop: "15px", // Add space for the X icon
              }}
            >
              {messages.map((message, index) => (
                <Text
                  key={index}
                  size="2"
                  style={{ display: "block", marginBottom: "5px" }}
                >
                  {message}
                </Text>
              ))}
              <div ref={messagesEndRef} />
            </Box>

            <Flex gap="1" align="center" style={{ marginBottom: "5px" }}>
              {CHAT_MODES.map(({ mode, label }) => (
                <button
                  key={mode}
                  onClick={() => setChatMode(mode)}
                  style={{
                    flex: 1,
                    padding: "5px 6px",
                    fontSize: "12px",
                    cursor: "pointer",
                    borderRadius: "4px",
                    border: "1px solid var(--ro-accent)",
                    background:
                      chatMode === mode ? "var(--ro-accent)" : "transparent",
                    color: chatMode === mode ? "black" : "var(--ro-accent)",
                    fontWeight: chatMode === mode ? 700 : 400,
                  }}
                >
                  {label}
                </button>
              ))}
            </Flex>

            <Flex gap="2" direction={isDM ? "row" : "column"}>
              {isDM && (
                <Box style={{ marginBottom: "5px", width: "30%" }}>
                  <input
                    type="text"
                    placeholder="Recipient"
                    value={dmRecipient}
                    onChange={(e) => setDmRecipient(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "8px",
                      backgroundColor: "#333",
                      color: "white",
                      border: "1px solid #555",
                      borderRadius: "4px",
                      boxSizing: "border-box",
                    }}
                  />
                </Box>
              )}

              <Box
                style={{ marginBottom: "5px", width: isDM ? "70%" : "100%" }}
              >
                <input
                  type="text"
                  placeholder="Type your message and press Enter"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleSendMessage}
                  disabled={isDM && !dmRecipient.trim()}
                  style={{
                    width: "100%",
                    padding: "8px",
                    backgroundColor: "#333",
                    color: "white",
                    border: "1px solid #555",
                    borderRadius: "4px",
                    boxSizing: "border-box",
                    opacity: isDM && !dmRecipient.trim() ? 0.5 : 1,
                  }}
                />
              </Box>
            </Flex>
          </>
        )}
      </Box>
    </ChatContext.Provider>
  );
}

export default ChatDialog;
